// src/components/YearChips.js
// Session 16 — Multi-year filter with clean "recent + More" layout.
// Shows only 4 most recent years inline; older years accessed via a "More ▾" modal.
//
// Usage:
//   const [years, setYears] = useState({ mode: 'single', value: 2026 });
//   <YearChips years={years} availableYears={[2026,2025,2024,...]}
//             onChange={setYears} dark={false} />
//
//   // when calling API:
//   const seasonParam = yearsToSeasonParam(years); // 'all' | '2026' | '2024,2025,2026'
//
// Props:
//   dark — if true, use light-text on dark-background styling (for navy headers)

import React, { useState } from 'react';

export function YearChips({ years, availableYears = [], onChange, dark = false }) {
  const mode = years?.mode || 'all';
  const selected = mode === 'single' ? [years.value]
                 : mode === 'multi'  ? (years.values || [])
                 : [];
  const [moreOpen, setMoreOpen] = useState(false);

  const RECENT_COUNT = 4;
  const recentYears = availableYears.slice(0, RECENT_COUNT);
  const earlierYears = availableYears.slice(RECENT_COUNT);
  const extraSelected = selected.filter(y => !recentYears.includes(y));
  const visibleYears = [...recentYears, ...extraSelected].sort((a,b)=>b-a);

  function handleAllTime() { onChange({ mode: 'all' }); }

  function handleYearTap(y) {
    if (mode === 'all') return onChange({ mode: 'single', value: y });
    if (mode === 'single') {
      if (years.value === y) return;
      return onChange({ mode: 'multi', values: [years.value, y].sort((a,b)=>b-a) });
    }
    const current = years.values || [];
    if (current.includes(y)) {
      const next = current.filter(v => v !== y);
      if (next.length === 0) return onChange({ mode: 'all' });
      if (next.length === 1) return onChange({ mode: 'single', value: next[0] });
      return onChange({ mode: 'multi', values: next.sort((a,b)=>b-a) });
    }
    onChange({ mode: 'multi', values: [...current, y].sort((a,b)=>b-a) });
  }

  const pillStyle = (active) => dark
    ? {
        padding: '6px 12px', borderRadius: 18, border: '1px solid',
        borderColor: active ? '#F5C518' : 'rgba(255,255,255,0.25)',
        background: active ? '#F5C518' : 'rgba(255,255,255,0.08)',
        color: active ? '#1a1a1a' : 'rgba(255,255,255,0.9)',
        fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.78rem',
        cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, transition: 'all 0.15s',
      }
    : {
        padding: '6px 12px', borderRadius: 18, border: '1px solid',
        borderColor: active ? '#1E3A6E' : '#d1d5db',
        background: active ? '#1E3A6E' : 'white',
        color: active ? 'white' : '#374151',
        fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.78rem',
        cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, transition: 'all 0.15s',
      };

  return (
    <>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <button onClick={handleAllTime} style={pillStyle(mode === 'all')}>All Time</button>
        {visibleYears.map(y => (
          <button key={y} onClick={() => handleYearTap(y)} style={pillStyle(selected.includes(y))}>{y}</button>
        ))}
        {earlierYears.length > 0 && (
          <button onClick={() => setMoreOpen(true)} style={{
            ...pillStyle(false),
            borderStyle: 'dashed',
            color: dark ? '#F5C518' : '#1E3A6E',
          }}>More ▾</button>
        )}
      </div>

      {moreOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 300,
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        }} onClick={(e) => { if (e.target === e.currentTarget) setMoreOpen(false); }}>
          <div style={{
            background: 'white', borderRadius: '18px 18px 0 0', width: '100%',
            maxWidth: 520, maxHeight: '85vh', overflowY: 'auto',
            padding: '18px 18px calc(18px + env(safe-area-inset-bottom, 0px))',
          }}>
            <div style={{ width: 40, height: 4, background: '#e5e7eb', borderRadius: 2, margin: '0 auto 14px' }} />
            <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1rem', color: '#1E3A6E', marginBottom: 4 }}>
              Select Seasons
            </div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: 14 }}>
              Tap any year to add or remove. Multiple years combine their stats.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              {availableYears.map(y => {
                const active = selected.includes(y);
                return (
                  <button key={y} onClick={() => handleYearTap(y)}
                    style={{
                      padding: '10px 0', borderRadius: 10, border: '2px solid',
                      borderColor: active ? '#1E3A6E' : '#e5e7eb',
                      background: active ? '#1E3A6E' : 'white',
                      color: active ? 'white' : '#374151',
                      fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.88rem',
                      cursor: 'pointer',
                    }}>{y}</button>
                );
              })}
            </div>
            <button onClick={() => setMoreOpen(false)} style={{
              width: '100%', marginTop: 16, padding: '11px', borderRadius: 10,
              border: 'none', background: '#1E3A6E', color: 'white',
              fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer',
            }}>Done</button>
          </div>
        </div>
      )}
    </>
  );
}

export function yearsToSeasonParam(years) {
  if (!years || years.mode === 'all') return 'all';
  if (years.mode === 'single') return String(years.value);
  if (years.mode === 'multi') return (years.values || []).join(',');
  return 'all';
}

export function yearsLabel(years) {
  if (!years || years.mode === 'all') return 'All Time';
  if (years.mode === 'single') return String(years.value);
  const vals = years.values || [];
  if (vals.length <= 3) return vals.join(', ');
  return `${vals.length} Seasons`;
}

export function yearStateFromScalar(scalar) {
  if (!scalar || scalar === 'all') return { mode: 'all' };
  if (String(scalar).includes(',')) {
    const vals = String(scalar).split(',').map(s => parseInt(s,10)).filter(Boolean);
    return vals.length === 1 ? { mode: 'single', value: vals[0] } : { mode: 'multi', values: vals };
  }
  return { mode: 'single', value: parseInt(scalar, 10) };
}

export default YearChips;
